import {ErrorList} from "mu-widget/lib/utils/ErrorList";
import {PlanItems} from "./components/admin/PlanItems";

export type Activity = {
    id: number;
    name: string;
    icon: string|null;
    numOrder: number;
    useWatch: boolean;
    requireNote: boolean;
    unit: string;
    options: ActivityFormOptions;
}

export type ActivityVariant = {
    id: number|null|string,
    name: string,
    icon: string|null,
    activityId: number|null,
    numOrder: number,
    sizes: ActivityVariantSize[],
    customValues: boolean,
    group: string;
    values: Nutrients;
}

/* export type JournalActivity = {
    id: number;
    journalId: number;
    activityId: number;
    userId: number;
    user: Account;
    dtStart: Date;
    value: number|null;
    note: string|null;
} */

export type Journal = {
    id: number;
    userId: number;
    user: Account;
}

export type Account = {
    id: number;
    email: string;
    name: string;
    isTrainer: boolean;
    // passwordHash: string;
    // llc: string|null;
}

export type ActivityFormOptions = {
    valueVisible: boolean;
    valueLabel: string | null;
    minValue?: number;
    maxValue?: number | null;
    inputType: 'number'|'range'|'time';
    useTimeWatcher: boolean;
    requireNote: boolean;
    unit: string|null;
    textValues: string[]|null;
    layout: 'default' | 'measure' | null;
    useInPlan: boolean;
    useVariant: boolean;
}

export type Id = number;

export type GenericResponse = {
    errors: ErrorList,
}

export type JournalActivity = {
    activity: string;
    trainerNoteIcon: string;
    trainerNote: string;
    id: number;
    dtStart: Date;
    value: number|null;
    note: string;
    activityId: number;
    activityVariantId: number;
    activityName: string;
    activityVariantName: string;
    activityOptions: {
        minValue: number | null;
        maxValue: number | null;
        inputType: string;
        useTimeWatcher: boolean;
        requireNote: boolean;
        unit: string | null;
        valueLabel: string;
        valueVisible: boolean;
        layout: "measure"|null;
    };
    icon: string | null;
    useTimeWatcher: boolean;
    values?: {
        value: number | null;
        activityVariantId: number;
    }[],
    customValues: Record<string, any>;
}

export type WatchedActivity = {
    name: string,
    journalActivityId: number,
    dtStart: Date,
};

export interface DayStats {
    recommendedValues: Nutrients;
    currentValues: Nutrients;
    energyFromActivities: number;
    basalEnergy: number; // Bazální metabolismus
    dayTimeRecommended: number;
}

export interface Nutrients {
    energy: number;
    protein: number;
    carbohydrate: number;
    fatSat?: number;
    fatUnsat?: number;
}

export class MyJournal {
    public activities: JournalActivity[];
    public watchedActivity: WatchedActivity|null;
    public dayStats: DayStats;
    public day: string;

    constructor(data: Record<string, any>) {
        (data.activities ?? []).forEach((item: any) => item.dtStart = new Date(item.dtStart));
        this.activities = data.activities ?? [];
        this.watchedActivity = data.watchedActivity
            ? {
                ...(data.watchedActivity ?? {}),
                dtStart: new Date(data.watchedActivity.dtStart),
            } : null;
        this.dayStats = data.dayStats;
    }
}

export type Plan = {
    items: PlanItem[];
    name: string;
    trainerNote: string|null;
}

export type PlanItemBasic = {
    id: number|null;
    activityId: number|null;
    activityVariantId: number|null;
    note: string|null;
    value: number;
    repeating: PlanItemRepeating;
    useTimeWatcher: boolean;
    unit: string;
}

export type PlanItem = PlanItemBasic & {
    planId: number;
    activity: string|null;
    activityVariant: string|null;
}

export type PlanItemRepeating = {
    dayOfWeek: number[],
    weekMod: string,
    secondsAM: number,
}

export type MyPlan = {
    id: number,
    name: string,
    active: boolean,
}

export type ActivityVariantSize = {
    value: number,
    label: string | null,
}

export type ActivityVariantValue = {
    value?: number,
    label: string,
    key: string,
    unit: string,
}

export const activityVariantValueTemplate: ActivityVariantValue[] = [
    { key: 'met', label: 'MET', unit: '' },
    { key: 'energy', label: 'Energie', unit: '#energy' },
    { key: 'protein', label: 'Bílkoviny', unit: 'g' },
    { key: 'carbohydrate', label: 'Sacharidy', unit: 'g' },
    { key: 'fatSat', label: 'Nasycený tuk', unit: 'g' },
    { key: 'fatUnsat', label: 'Nenasycený tuk', unit: 'g' },
];

export const foodStatComponents = [
    { key: 'energy', label: 'Energie', unit: '#energy' },
    { key: 'protein', label: 'Bílkoviny', unit: 'g' },
    { key: 'carbohydrate', label: 'Sacharidy', unit: 'g' },
    { key: 'fatSat', label: 'Nasycený tuk', unit: 'g' },
    { key: 'fatUnsat', label: 'Nenasycený tuk', unit: 'g' },
];

export type DayFoodPlan = {
    times: number[],
    energies: number[],
}

export type ClientData = {
    id: number;
    accountId: number;
    dtBirth: string;
    gender: number;
    height: number;
    mass: number;
    palLevelWork: number;
    palLevelSport: number;
    profile: string;
    energyUnit: string;
    tune: Record<string, any> | null;
    basalEnergyIntake: number | null;
    recEnergyIntake: number | null;
    lockAttributes: string[];
    dayFoodPlan: DayFoodPlan;
    timezone: string;
    foodContentCoefficients: any,
}

export type FoundActivityVariants = {
    letters: string[],
    categories: { value: string, text: string }[],
    items: ActivityVariant[],
    isLastLoad: boolean,
}